import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Settings,
  BarChart3,
  LogOut,
  Menu,
  X,
  Shield,
  ArrowLeftRight,
  RefreshCw,
  Package,
  PackageSearch,
  PackageCheck,
  FileText,
  Users,
  User as UserIcon,
  Truck,
} from "lucide-react";
import { useState } from "react";

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const navigation =
    user?.role === "staff"
      ? [
          { name: "Dashboard", href: "/", icon: BarChart3 },
          { name: "EDI Routing info", href: "/edi-routing", icon: Truck },
        ]
      : [
          { name: "Dashboard", href: "/", icon: BarChart3 },
          { name: "Product Sync", href: "/product-sync", icon: RefreshCw },
          { name: "Inventory Sync", href: "/inventory-sync", icon: Package },
          { name: "Supplier Shipments", href: "/supplier-shipments", icon: PackageSearch },
          { name: "Customer Shipments", href: "/customer-shipments", icon: PackageCheck },
          { name: "EDI Shipments", href: "/edi-shipments", icon: FileText },
          { name: "EDI Routing info", href: "/edi-routing", icon: Truck },
          ...(user?.role === "superadmin"
            ? [
                {
                  name: "User Management",
                  href: "/users",
                  icon: Users,
                },
              ]
            : []),
          { name: "Configuration", href: "/configuration", icon: Settings },
        ];

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    navigate("/login");
    setShowLogoutConfirm(false);
  };

  const isActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    if (href.includes("?")) {
      const [path, query] = href.split("?");
      return (
        location.pathname === path &&
        location.search.includes(query.split("=")[1])
      );
    }
    return location.pathname.startsWith(href);
  };

  const handleNavigation = (href: string) => {
    if (href.includes("?")) {
      const [path, query] = href.split("?");
      navigate(path + "?" + query);
    } else {
      navigate(href);
    }
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center">
              <span>Fulfil</span>
              <ArrowLeftRight className="mx-2 h-5 w-5 text-gray-700" />
              <span>ShipHero</span>
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className={`group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </button>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            <button
              onClick={() => handleNavigation("/profile")}
              className={`group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md ${
                isActive("/profile")
                  ? "bg-primary-100 text-primary-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <UserIcon className="mr-3 h-5 w-5" />
              Profile
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center">
              <span>Fulfil</span>
              <ArrowLeftRight className="mx-2 h-5 w-5 text-gray-700" />
              <span>ShipHero</span>
            </h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavigation(item.href)}
                  className={`group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </button>
              );
            })}
          </nav>
          <div className="mt-auto border-t border-gray-200 px-2 py-4">
            <button
              onClick={() => handleNavigation("/profile")}
              className={`group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md ${
                isActive("/profile")
                  ? "bg-primary-100 text-primary-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <UserIcon className="mr-3 h-5 w-5" />
              Profile
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="text-sm text-gray-700">
                Welcome, {user?.name || user?.username}
                {/* {user?.is_admin && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </span>
                )} */}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 text-sm text-gray-700 hover:text-gray-900"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Logout</h3>
              <p className="text-sm text-gray-700">
                Are you sure you want to log out?
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
